import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { object } from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import { Banner, FormCheckbox, RadioGroup } from 'components';
import { addUuid, formatLocalizedCurrency } from 'helpers';
import { routes } from 'router';
import { appService } from 'services';

import { iconCB, iconMaestro, iconMastercard, iconVisa } from 'assets/img/icons';

const PaymentMethodsIcons = () => {
  const { t } = useTranslation();

  const cards = [
    { src: iconCB, alt: t('purchase:cards.cb'), name: 'cb' },
    { src: iconMastercard, alt: t('purchase:cards.mastercard'), name: 'mastercard' },
    { src: iconMaestro, alt: t('purchase:cards.maestro'), name: 'maestro' },
    { src: iconVisa, alt: t('purchase:cards.visa'), name: 'visa' }
  ];

  return (
    <div className="flex flex-wrap items-center gap-1">
      {cards.map((card) => (
        <div
          key={`card-icon-${card.name}`}
          className="bg-white border border-gray-300 rounded-[0.25rem] overflow-hidden w-10 h-6"
        >
          <img
            src={card.src}
            alt={card.alt}
            width="38"
            height="22"
            className={clsx(
              '"w-full h-full object-cover',
              ['mastercard', 'maestro'].includes(card.name) ? 'p-[2px]' : 'p-0'
            )}
          />
        </div>
      ))}
    </div>
  );
};

const PaymentMethodsForm = ({ cart }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { setValue } = useFormContext();
  const typeItem = searchParams.get('typeItem');

  const storedNewsletterChoice = useSelector((state) =>
    typeItem ? state.travelPurchase.newsletter : state.leisurePurchase.newsletter
  );

  const storedPaymentMethodChoice = useSelector((state) =>
    typeItem ? state.travelPurchase.paymentMethod : state.leisurePurchase.paymentMethod
  );

  const storedDepositChoice = useSelector((state) =>
    typeItem ? state.travelPurchase.depositChoice : state.leisurePurchase.depositChoice
  );

  const updatedCart = useSelector((state) => state.cart.cart);

  const showCancellationCheckbox = useMemo(() => {
    // Show cancellation checkboxes only if the cart has at least one giftcard
    return cart?.lines?.some((line) => line.buybox);
  }, [cart]);

  useEffect(() => {
    if (storedDepositChoice) {
      setValue('validation.depositChoice', storedDepositChoice);
    } else if (!isEmpty(cart?.availablePaymentMode?.withDeposit)) {
      setValue('validation.depositChoice', 'withDeposit');
    } else {
      setValue('validation.depositChoice', 'withoutDeposit');
    }
  }, [cart]);

  useEffect(() => {
    if (storedNewsletterChoice) {
      setValue('validation.newsletter', storedNewsletterChoice);
    }
    if (storedPaymentMethodChoice) {
      setValue('validation.paymentMethod', storedPaymentMethodChoice);
    }
  }, []);

  const paymentOptions = useMemo(() => {
    const depositAmount =
      (isEmpty(updatedCart)
        ? cart?.availablePaymentMode?.withDeposit?.amount
        : updatedCart?.availablePaymentMode?.withDeposit?.amount || 0) / 100;

    const options = [
      {
        name: 'validation.depositChoice',
        value: 'withDeposit',
        label: t('purchase:payment.withDeposit', {
          deposit: formatLocalizedCurrency(depositAmount)
        })
      },
      {
        name: 'validation.depositChoice',
        value: 'withoutDeposit',
        label: t('purchase:payment.withoutDeposit')
      }
    ];

    return addUuid(options, 'payment-radio-');
  }, [cart, updatedCart]);

  const paymentMethods = useMemo(() => {
    const availableMethods = cart?.availablePaymentMode?.withoutDeposit?.paymentMethods || [];

    return availableMethods.map(
      (method, index) => {
        let labelContent = t(`purchase:payment:paymentMethods.${method}`);

        if (method !== 'card') {
          labelContent = (
            <div>
              <div className="flex items-center mt-2">
                <span className="uppercase mr-2">{labelContent}</span>
              </div>
              <span className=" text-gray-600">{t('purchase:payment.paymentMethods.cardComplementText')}</span>
            </div>
          );
        } else {
          labelContent = (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="uppercase mr-2">{labelContent}</span>
              <PaymentMethodsIcons />
            </div>
          );
        }

        return {
          name: 'validation.paymentMethodChoice',
          value: method,
          uuid: `payment-method-radio-${index}`,
          content: () => labelContent
        };
      },
      [cart]
    );
  });

  return (
    <div className="mt-8">
      <h2 className="mb-6 underlined-blue after:hidden h3 lg:after:block lg:h2">{t('purchase:payment.title')}</h2>
      <form className="flex flex-col gap-4 mb-4 travel-payment-step">
        {cart?.availablePaymentMode?.withDeposit && (
          <RadioGroup
            radioOptions={paymentOptions}
            labelHidden
            className="lg:flex-row"
            name="validation.depositChoice"
          />
        )}

        <RadioGroup
          radioOptions={paymentMethods}
          labelHidden
          className="radio-container card-radio"
          name="validation.paymentMethod"
        />

        <FormCheckbox
          name="validation.salesTerms"
          labelI18nKey="purchase:payment.acceptSalesTerms"
          labelClassName="flex flex-wrap"
          link={{
            url: `${routes.termsOfSales.path}`,
            target: '_blank',
            className: 'underline',
            i18nValues: { appName: appService.brand() }
          }}
        />
        <FormCheckbox
          name="validation.specificSalesTerms"
          labelI18nKey="purchase:payment.acceptSpecificSalesTerms"
          labelClassName="flex flex-wrap"
          link={{
            url: `${routes.termsOfSales.path}`,
            target: '_blank',
            className: 'underline',
            i18nValues: { appName: appService.brand() }
          }}
        />
        {showCancellationCheckbox && (
          <>
            <FormCheckbox
              name="validation.cancellationAgreement"
              labelI18nKey="purchase:payment.cancellationAgreement"
            />
            <FormCheckbox
              name="validation.cancellationClauseAccepted"
              labelI18nKey="purchase:payment.cancellationClause"
            />
          </>
        )}
        <FormCheckbox
          name="validation.newsletter"
          labelI18nKey="purchase:payment.newsletter"
          labelI18nValues={{ appName: appService.brand() }}
        />
      </form>
      <Banner type="warning" icon={{ name: 'circle-info', style: 'fad' }} className="lg:mb-8">
        <div>
          <Trans
            i18nKey="purchase:validation.reminderEmail"
            values={{ appName: appService.brand() }}
            className="text-gray-600 md:w-4/5"
          >
            <br />
            <br />
            <Link to={routes.terms.path} className="underline hover:no-underline" target="_blank">
              {t('buttons.clickHere')}
            </Link>
          </Trans>
        </div>
      </Banner>
    </div>
  );
};

PaymentMethodsForm.propTypes = {
  cart: object
};

export default PaymentMethodsForm;
